import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  AfterViewInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import intlTelInput, { Iti } from 'intl-tel-input';

@Directive({
  selector: '[appIntlTelInput]',
  standalone: true,
})
export class IntlTelInputDirective implements AfterViewInit, OnDestroy {
  @Input() initialCountry = 'in';
  @Input() countryOrder: string[] = ['in', 'ae', 'sa', 'id', 'my'];
  @Output() countryChange = new EventEmitter<{ dialCode: string; iso2: string }>();

  private iti?: Iti;
  private isBrowser: boolean;
  private countryListener?: () => void;
  private clickOffListener?: (e: MouseEvent) => void;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit() {
    if (!this.isBrowser) return;

    this.iti = intlTelInput(this.el.nativeElement, {
      initialCountry: this.initialCountry,
      separateDialCode: true,
      formatOnDisplay: true,
      nationalMode: false,
      autoHideDialCode: false,
      countryOrder: this.countryOrder,
      utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@26.0.9/build/js/utils.js',
    } as Parameters<typeof intlTelInput>[1]);

    this.countryListener = () => {
      const data = this.iti!.getSelectedCountryData();
      this.countryChange.emit({
        dialCode: `+${data.dialCode}`,
        iso2: (data.iso2 || '').toUpperCase(),
      });
    };

    this.el.nativeElement.addEventListener('countrychange', this.countryListener);
    this.countryListener();

    // The library's own "click outside to close" listener bubbles up to
    // document, but the chat widget stops click propagation on the panel
    // (so clicks inside it don't dismiss the whole chat) - that also
    // swallows the dropdown's close signal. Use a capture-phase listener
    // instead, which always fires before any bubble-phase stopPropagation.
    this.clickOffListener = (e: MouseEvent) => {
      if (!this.iti) return;

      const target = e.target as Node;

      // Without a configured `dropdownContainer`, the country list is
      // appended inside `.iti__country-container` (ui.dropdown stays null -
      // that's only created for the portaled/fullscreen popup case). Check
      // both so a click on an actual list item is never misread as "outside"
      // and closed before the library's own selection handler runs.
      // Not part of the library's public `Iti` type - these are undocumented internals.
      const internals = this.iti as unknown as {
        ui?: { countryContainer?: HTMLElement; dropdown?: HTMLElement };
        _closeDropdown?: () => void;
      };
      const countryContainerEl = internals.ui?.countryContainer;
      const portalDropdownEl = internals.ui?.dropdown;
      const insideDropdown =
        !!countryContainerEl?.contains(target) || !!portalDropdownEl?.contains(target);
      const insideInput = this.el.nativeElement.contains(target);

      // _closeDropdown() no-ops if the dropdown is already closed, so it's
      // safe to call on every outside click without tracking open state.
      if (!insideDropdown && !insideInput) {
        internals._closeDropdown?.();
      }
    };
    document.addEventListener('click', this.clickOffListener, true);
  }

  ngOnDestroy() {
    if (this.iti) this.iti.destroy();
    if (this.countryListener) {
      this.el.nativeElement.removeEventListener('countrychange', this.countryListener);
    }
    if (this.clickOffListener) {
      document.removeEventListener('click', this.clickOffListener, true);
    }
  }
}
